import { Suspense, lazy, useState } from 'react';
import { useSearchParams } from 'react-router';
import { REQUIRED_CONTENT, type PlaceContent } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { PlaceEditor } from './place-editor';

// the 3D designer brings three.js with it: loaded only when someone opens it
const PlaceDesigner = lazy(() => import('./place-designer').then((module) => ({ default: module.PlaceDesigner })));

const ID = /^[a-z0-9_]+$/;

// Places: every place on the map, what it looks like inside and what can be done there. Nothing about them is
// in the game's code: this page is where they are made and changed (as a draft, then published).
export function PlacesPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const [params, setParams] = useSearchParams();
  const open = params.get('place');
  const [adding, setAdding] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [copyFrom, setCopyFrom] = useState('cafe');
  const working = editor.working;
  const place = working?.places.find((item) => item.id === open) ?? null;
  const canWrite = can('content.write');
  const add = async () => {
    if (!working) return;
    const id = newId.trim();
    if (!ID.test(id)) throw new Error('The id uses lower-case letters, numbers and _ only (it never changes).');
    if (working.places.some((item) => item.id === id)) throw new Error('A place already has that id.');
    if (!newName.trim()) throw new Error('Give the place a name.');
    const model = working.places.find((item) => item.id === copyFrom) ?? working.places[0]!;
    // a new place starts with the inside of a place like it (spots start empty), hidden until it is ready
    const made: PlaceContent = {
      ...structuredClone(model),
      id,
      name: newName.trim(),
      description: '',
      things: [],
      badge: undefined,
      comingSoon: undefined,
      visible: false,
      sortOrder: Math.max(0, ...working.places.map((item) => item.sortOrder)) + 1,
      interior: {
        ...structuredClone(model.interior),
        sign: model.interior.sign ? { ...model.interior.sign, text: newName.trim() } : null,
        spots: [],
      },
    };
    editor.edit((content) => content.places.push(made));
    setParams({ place: id });
  };
  return (
    <section>
      <PageHeader
        title={place ? place.name : 'Places'}
        description={
          place
            ? `${place.area} · id ${place.id}`
            : 'Every place on the map: where it is, what it looks like inside and what players can do there. Changes are a draft until they are published.'
        }
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && !place && (
        <>
          {canWrite && (
            <div className="admin-toolbar">
              <Button
                type="button"
                onClick={() => {
                  setNewId('');
                  setNewName('');
                  setAdding(true);
                }}
              >
                Add a place
              </Button>
            </div>
          )}
          <DataTable
            rows={[...working.places].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))}
            rowKey={(row) => row.id}
            onRowClick={(row) => setParams({ place: row.id })}
            columns={[
              {
                key: 'name',
                header: 'Place',
                render: (row) => (
                  <span className="content-place-name">
                    <strong>
                      <span aria-hidden="true">{row.icon}</span> {row.name}
                    </strong>
                    <span className="admin-muted">{row.area}</span>
                  </span>
                ),
              },
              {
                key: 'shown',
                header: 'On the map',
                render: (row) =>
                  row.visible ? <Badge variant="secondary">Shown</Badge> : <Badge variant="outline">Hidden</Badge>,
              },
              {
                key: 'activities',
                header: 'Activities',
                render: (row) => working.activities.filter((activity) => activity.placeId === row.id).length,
              },
              { key: 'spots', header: 'Spots', render: (row) => row.interior.spots.length },
              { key: 'inside', header: 'Inside', render: (row) => (row.interior.outdoor ? 'Outdoors' : 'Indoors') },
            ]}
          />
        </>
      )}
      {working && place && params.get('design') === '1' && (
        <Suspense fallback={<p className="admin-muted">Opening the designer…</p>}>
          <PlaceDesigner
            editor={editor}
            place={place}
            readOnly={!canWrite}
            onBack={() => setParams({ place: place.id })}
          />
        </Suspense>
      )}
      {working && place && params.get('design') !== '1' && (
        <PlaceEditor
          editor={editor}
          place={place}
          readOnly={!canWrite}
          required={(REQUIRED_CONTENT.places as readonly string[]).includes(place.id)}
          onBack={() => setParams({})}
          onDesign={() => setParams({ place: place.id, design: '1' })}
        />
      )}
      <FormDialog
        open={adding}
        title="Add a place"
        description="It starts hidden, with the inside of a place like it. Fill it in, then show it on the map and publish."
        submitLabel="Add"
        onSubmit={add}
        onClose={() => setAdding(false)}
      >
        <div className="admin-field">
          <Label htmlFor="place-id">Id (never changes)</Label>
          <Input id="place-id" value={newId} maxLength={40} onChange={(event) => setNewId(event.target.value)} />
        </div>
        <div className="admin-field">
          <Label htmlFor="place-name">Name</Label>
          <Input id="place-name" value={newName} maxLength={60} onChange={(event) => setNewName(event.target.value)} />
        </div>
        <div className="admin-field">
          <Label htmlFor="place-like">Start its inside like</Label>
          <select
            id="place-like"
            className="content-select"
            value={copyFrom}
            onChange={(event) => setCopyFrom(event.target.value)}
          >
            {working?.places.map((item) => (
              <option key={item.id} value={item.id}>
                {item.icon} {item.name}
              </option>
            ))}
          </select>
        </div>
      </FormDialog>
    </section>
  );
}
